import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { GraduationCap, CheckCircle2, ArrowRight } from "lucide-react";

const DESK_IMG = "https://images.pexels.com/photos/5833750/pexels-photo-5833750.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const MODULES = [
  { n: "01", t: "Market Structure Mastery", d: "Read HH/HL, break of structure and change of character across timeframes." },
  { n: "02", t: "Smart Money Concepts", d: "Order blocks, fair value gaps, liquidity sweeps and institutional footprints." },
  { n: "03", t: "Risk & Position Sizing", d: "Protect capital first. Build a risk model that survives losing streaks." },
  { n: "04", t: "Trading Psychology", d: "Engineer the discipline and patience that separates pros from gamblers." },
];

export const Academy = ({ onJoin }) => {
  return (
    <section id="academy" className="relative py-20 lg:py-28 grain-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="font-data text-xs uppercase tracking-[0.2em] text-brand">◆ Academy Vault</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Train like an institution</h2>
            <p className="mt-4 text-slate-400 text-base leading-relaxed">
              A structured curriculum built by desk traders. From market structure to psychology — everything you need to
              trade the forex markets with a genuine edge.
            </p>

            <div className="mt-8 space-y-4">
              {MODULES.map((m, i) => (
                <motion.div
                  key={m.n}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex gap-4 group"
                >
                  <span className="font-data text-lg font-bold text-brand/70 group-hover:text-brand transition-colors">{m.n}</span>
                  <div className="flex-1 pb-4 border-b border-white/5">
                    <h4 className="text-base font-semibold text-slate-100 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-gain" /> {m.t}
                    </h4>
                    <p className="text-sm text-slate-400 mt-1">{m.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Button data-testid="academy-cta-enroll" onClick={onJoin} className="mt-8 bg-brand hover:bg-brand-hover text-white font-semibold h-12 px-6 gap-2">
              Enroll Free <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden card-surface">
              <img src={DESK_IMG} alt="Professional trading desk" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070A0E] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 rounded-xl bg-[#070A0E]/80 backdrop-blur-md border border-white/10 p-4">
                  <div className="h-11 w-11 rounded-lg bg-brand/15 border border-brand/40 grid place-items-center">
                    <GraduationCap className="h-6 w-6 text-brand" />
                  </div>
                  <div>
                    <div className="font-data text-2xl font-bold text-white">12,400+</div>
                    <div className="font-data text-[11px] uppercase tracking-widest text-slate-400">Traders trained · 2026</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
